import { Offer } from '../../../../services/exports/Interfaces';
import { CompleteBasketItem } from '../../../../providers/BasketProvider';
import { IOfferHandler } from '../../types';
import { useTranslation } from 'react-i18next';

const useFreeItemByProducts: IOfferHandler = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Features:Offers:OfferBadge' });
  const isCriteriaItem = (offer: Offer, item: CompleteBasketItem) => offer.details.products.includes(item.entry.id);
  const isUnlocked = (offer: Offer, items: CompleteBasketItem[]) => items.some((item) => isCriteriaItem(offer, item));
  const isEligibleItem = (offer: Offer, item: CompleteBasketItem): boolean =>
    offer.details.free_products.includes(item.entry.id);
  const isEligible = (offer: Offer, items: CompleteBasketItem[]) => items.some((item) => isEligibleItem(offer, item));
  const getFreeItemsCount = (offer: Offer, items: CompleteBasketItem[]) =>
    items.reduce((acc, current) => (isCriteriaItem(offer, current) ? acc + current.quantity : acc), 0);
  const sortItemsByProfit = (offer: Offer, items: CompleteBasketItem[]) =>
    items
      .filter((item) => isEligibleItem(offer, item))
      .sort((a, b) => {
        const profitA = a.entry.actual_price;
        const profitB = b.entry.actual_price;

        return profitB - profitA;
      });
  const getProfit = (offer: Offer, items: CompleteBasketItem[]): number => {
    const sortedByProfitItems = sortItemsByProfit(offer, items);

    if (!sortedByProfitItems.length) {
      return 0;
    }

    let freeItemsCount = getFreeItemsCount(offer, items);
    let totalProfit = 0;

    while (freeItemsCount !== 0 && sortedByProfitItems.length) {
      const item = sortedByProfitItems.shift();

      if (freeItemsCount >= item.quantity) {
        totalProfit += item.quantity * item.entry.actual_price;
        freeItemsCount -= item.quantity;
        continue;
      }

      totalProfit += freeItemsCount * item.entry.actual_price;
      freeItemsCount = 0;
    }

    return totalProfit;
  };
  const applyToItem = (offer: Offer, items: CompleteBasketItem[], item: CompleteBasketItem): CompleteBasketItem => {
    const sortedByProfitItems = sortItemsByProfit(offer, items);
    let freeItemsCount = getFreeItemsCount(offer, items);

    while (freeItemsCount !== 0 && sortedByProfitItems.length) {
      const sortedItem = sortedByProfitItems.shift();
      let actualPrice: number;

      if (freeItemsCount >= sortedItem.quantity) {
        actualPrice = sortedItem.actual_price - sortedItem.quantity * sortedItem.entry.actual_price;
        freeItemsCount -= sortedItem.quantity;
      } else {
        actualPrice = sortedItem.actual_price - freeItemsCount * sortedItem.entry.actual_price;
        freeItemsCount = 0;
      }

      if (item.original_index === sortedItem.original_index) {
        return {
          ...item,
          badges: [...item.badges, t(offer.kind)],
          actual_price: actualPrice,
        };
      }
    }

    return item;
  };
  const apply =
    (offer: Offer, items: CompleteBasketItem[]) =>
    (item: CompleteBasketItem): CompleteBasketItem => {
      if (!isEligibleItem(offer, item)) {
        return item;
      }

      return applyToItem(offer, items, item);
    };

  return (offer, items) => ({
    apply: apply(offer, items),
    isEligible: isEligible(offer, items),
    isUnlocked: isUnlocked(offer, items),
    profit: getProfit(offer, items),
  });
};

export default useFreeItemByProducts;
